// SPDX-License-Identifier: AGPL-3.0-or-later
/**
 * Codes that represent a clinically-significant signal a clinician
 * should look at before signing off on a response — as distinct from
 * data-quality issues (missing required field, malformed phone, DOB
 * typo) which are about form integrity rather than the patient's
 * clinical picture. The `flagged` flag on a questionnaire_response
 * row is set when ANY of these codes appears in the submission's
 * findings.
 *
 * Curating this list well requires clinician input — the default
 * conservative cut is:
 *   - `contradiction` (cross-field rules — usually clinically authored)
 *   - `eligibility`   (explicit clinical-eligibility rules)
 *   - `stale`         (answer outside the freshness window — clinical
 *                      relevance because the EMR record may have
 *                      drifted since)
 *
 * Anything else flows through as a Finding the UI surfaces inline
 * for the patient or per-field for the clinician, but doesn't bump
 * the response-level "needs review" badge.
 */
/**
 * Stable enumeration of every FindingCode the engine can emit.
 * Exported so consumers (settings UI, zod schemas, validation tests)
 * can enumerate the universe without re-declaring it.
 */
export const ALL_FINDING_CODES = [
    'required',
    'type',
    'regex',
    'min',
    'max',
    'contradiction',
    'incomplete',
    'stale',
    'eligibility',
    'async',
    'email-no-mx',
    'email-disposable',
    'phone-invalid',
    'phone-unparseable',
    'phone-twilio-invalid',
    'phone-twilio-line-type',
    'dob-future',
    'dob-implausible',
];
const CLINICALLY_SIGNIFICANT_CODES = new Set([
    'contradiction',
    'eligibility',
    'stale',
]);
/**
 * Default partition exposed for consumers (e.g. settings UI) that
 * need to show "which codes are currently clinical?" without
 * importing the constant.
 */
export function defaultClinicallySignificantCodes() {
    return CLINICALLY_SIGNIFICANT_CODES;
}
/**
 * Is `code` clinically significant?  When `override` is supplied,
 * the per-org / per-call set wins; otherwise the default is used.
 * The org-configurable partition (intakeform-preview-6hy) reads
 * `organization.settings.clinical_significance_codes` and passes
 * it through here.
 */
export function isClinicallySignificant(code, override) {
    const set = override ?? CLINICALLY_SIGNIFICANT_CODES;
    return set.has(code);
}
//# sourceMappingURL=types.js.map